import { useLiveQuery } from 'dexie-react-hooks';
import { useLearner } from '@/app/useAppState';
import { STAGE } from '@/components/ui/Badges';
import { Loading } from '@/components/ui/Loading';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { ScreenHeader } from '@/components/ui/ScreenHeader';
import { curriculum } from '@/content/curriculum';
import { useI18n } from '@/i18n/context';
import { cn } from '@/lib/cn';
import { db } from '@/services/db/database';
import { ACHIEVEMENTS } from './catalog';
import { achievementDescription, achievementTitle } from './text';

export function RewardsScreen() {
  const learner = useLearner();
  const { t, l, formatDate } = useI18n();
  const data = useLiveQuery(async () => {
    const [completed, attempts, mastery, owned] = await Promise.all([
      db.sessions.where('[learnerId+status]').equals([learner.id, 'completed']).toArray(),
      db.attempts.where('learnerId').equals(learner.id).toArray(),
      db.mastery.where('learnerId').equals(learner.id).toArray(),
      db.achievements.where('learnerId').equals(learner.id).toArray(),
    ]);
    return {
      stats: { completed, attempts, mastery },
      owned: new Map(owned.map((a) => [a.achievementId, a.unlockedAt])),
    };
  }, [learner.id]);
  if (!data) return <Loading label={t('app.loading')} />;

  const flowers = data.stats.mastery.filter((m) => m.status === 'provisionalMastery');
  const earned = ACHIEVEMENTS.filter((a) => data.owned.has(a.id));
  const locked = ACHIEVEMENTS.filter((a) => !data.owned.has(a.id));

  return (
    <div className="flex flex-col gap-8">
      <ScreenHeader title={t('rewards.title')} />

      <section aria-labelledby="garden" className="flex flex-col gap-3">
        <h2 id="garden" className="text-xl font-extrabold">
          {t('rewards.garden')}
        </h2>
        {flowers.length === 0 ? (
          <p className="rounded-2xl bg-brand-50 p-4 text-ink-muted">{t('rewards.gardenEmpty')}</p>
        ) : (
          <ul className="flex flex-wrap gap-3 rounded-[var(--radius-card)] bg-brand-50 p-4">
            {flowers.map((m) => {
              const objective = curriculum.objective(m.objectiveId);
              return (
                <li key={m.objectiveId} className="flex flex-col items-center gap-1 text-center">
                  <span aria-hidden className="emoji text-4xl">
                    {STAGE.provisionalMastery.emoji}
                  </span>
                  <span className="max-w-28 text-xs font-bold">
                    {objective ? l(objective.title) : m.objectiveId}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {[
        { title: t('rewards.earned'), list: earned },
        { title: t('rewards.locked'), list: locked },
      ].map(({ title, list }) =>
        list.length === 0 ? null : (
          <section key={title} className="flex flex-col gap-3">
            <h2 className="text-xl font-extrabold">{title}</h2>
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {list.map((achievement) => {
                const unlockedAt = data.owned.get(achievement.id);
                const [current, target] = achievement.progress(data.stats);
                return (
                  <li
                    key={achievement.id}
                    className={cn(
                      'flex flex-col items-center gap-2 rounded-[var(--radius-card)] p-4 text-center shadow-[var(--shadow-card)]',
                      unlockedAt ? 'bg-sun-100' : 'bg-surface',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn('emoji text-5xl', !unlockedAt && 'opacity-40 grayscale')}
                    >
                      {achievement.icon}
                    </span>
                    <span className="font-extrabold">{achievementTitle(t, achievement.id)}</span>
                    <span className="text-sm text-ink-muted">
                      {achievementDescription(t, achievement.id)}
                    </span>
                    {unlockedAt ? (
                      <span className="text-xs font-bold text-sun-700">
                        {formatDate(unlockedAt)}
                      </span>
                    ) : (
                      <div className="flex w-full flex-col gap-1">
                        <ProgressBar
                          value={current}
                          max={target}
                          tone="sun"
                          label={t('rewards.progress', {
                            current: Math.min(current, target),
                            target,
                          })}
                        />
                        <span className="text-xs font-bold text-ink-muted">
                          {t('rewards.progress', { current: Math.min(current, target), target })}
                        </span>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ),
      )}
    </div>
  );
}
